// core
import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';

// models
import {User} from './models/user.model';

// services
import {UserRegistrationServiceService} from './service/user-registration-service.service';
import {PasswordValidation} from '../shared/services/password-validation';
import {UtilsService} from '../utils/utils.service';
import {ErrorMessageService} from '../core/alert-message/alert-message.service';

// constants
import {AppConstants} from '../utils/constants';

// 3'rd party
import {ReCaptchaV3Service} from 'ngx-captcha';
import {DataPrivacyNotice} from './models/data-privacy-notice.model';
import {Observable} from 'rxjs';
import {environment} from '../../environments/environment';

declare var $: any;

@Component({
    selector: 'app-user-registration',
    templateUrl: './user-registration.component.html',
    styleUrls: ['./user-registration.component.scss'],
    standalone: false
})

export class UserRegistrationComponent implements OnInit {
  form: UntypedFormGroup;

  public languageIconPath = 'assets/images/globe.png';
  public selectedLanguage;
  public selectedCountry;
  public languageSelectorData = AppConstants.globalLanguageSelectorData;
  public countrySelectorData = AppConstants.globalCountrySelectorData;

  public customerNumberReadonly = false;
  public customerNameReadonly = false;
  public customerNumberRequired = false;
  public customerNameRequired = false;
  public phoneNumberRequired = false;
  public postalCodeRequired = false;
  public whatLanguage;
  public whatCountry;
  private englishLanguage = AppConstants.englishLanguageCode;
  private germanLanguage = AppConstants.germanLanguageCode;
  private frenchLanguage = AppConstants.frenchLanguageCode;
  private dutchLanguage = AppConstants.dutchLanguageCode;

  public englishTitles: any;
  public languages: any;
  public countries: any;
  public targetCategories: any;

  public user: User = new User();

  public siteKey: string;

  private listOfCategories: Array<any> = new Array<any>();
  private listOfTitles: Array<any> = new Array<any>();
  private listOfLanguages: Array<any> = new Array<any>();
  private listOfCountries: Array<any> = new Array<any>();

  public loading = false;

  private dataPrivacyNotice: Observable<DataPrivacyNotice>;

  constructor(private _userRegistrationService: UserRegistrationServiceService,
              private _formBuilder: UntypedFormBuilder,
              private _utilsService: UtilsService,
              private _translateService: TranslateService,
              private errorMessageService: ErrorMessageService,
              private _route: Router,
              private _activatedRoute: ActivatedRoute,
              private _changeDetectorRef: ChangeDetectorRef,
              private recaptchaV3Service: ReCaptchaV3Service,
              ) {

    _translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.whatLanguage = event.lang;
      this.listOfTitles = this._utilsService.initializeSelectOrMultiselect(this.englishTitles);
      this.listOfLanguages = this._utilsService.initializeSelectOrMultiselect(this.languages);
      this.listOfCountries = this._utilsService.initializeSelectOrMultiselect(this.countries);
      this.listOfCategories = this._utilsService.initializeSelectOrMultiselect(this.targetCategories);
      this.dataPrivacyNotice = this._userRegistrationService.getDataPrivacyNotice(this.whatLanguage);
    });

    this.form = _formBuilder.group({
      customerCategory: [
        {
          value: null,
          disabled: false
        },
        Validators.required
      ],
      gRecaptchaResponse: [
        {
          value: null,
          disabled: false
        },
        Validators.nullValidator
      ],
      customerName: [
        {
          value: null,
          disabled: false
        },
        Validators.compose([
          Validators.maxLength(50)
        ])
      ],
      customerNumber: [
        {
          value: null,
          disabled: false
        },
        Validators.compose([
          Validators.pattern(this._utilsService.getOnlyNumbersRegex()),
          Validators.maxLength(10)
        ])
      ],
      phoneNumber: [
        {
          value: null,
          disabled: false
        },
        Validators.compose([
          Validators.pattern(this._utilsService.getPhoneNumberRegex()),
          Validators.maxLength(20)
        ])
      ],
      salutation: [
        {
          value: null,
          disabled: false
        }
      ],
      firstName: [
        {
          value: null,
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(20)
        ])
      ],
      lastName: [
        {
          value: null,
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(40)
        ])
      ],
      email: [
        {
          value: null,
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.pattern(this._utilsService.getEmailRegexPattern()),
          Validators.maxLength(50)
        ])
      ],
      password: [
        {
          value: null,
          disabled: false
        },
        Validators.compose([
          Validators.required,
          Validators.pattern(this._utilsService.getPasswordRegex()),
          Validators.minLength(8)
        ])
      ],
      confirmPassword: [
        {
          value: null,
          disabled: false
        },
        Validators.required
      ],
      language: [
        {
          value: null,
          disabled: false
        },
        Validators.required
      ],
      countryCode: [
        {
          value: null,
          disabled: false
        },
        Validators.nullValidator
      ],
      postalCode: [
        {
          value: null,
          disabled: false
        },
        Validators.compose([
          Validators.maxLength(10),
          Validators.minLength(2),
          Validators.pattern(this._utilsService.getPostalCodeRegex())
        ])
      ],
      subsribeToNewsletter: [
        {
          value: false,
          disabled: false
        }
      ],
      privacy: [
        {
          value: false,
          disabled: false
        },
        Validators.pattern('true')
      ]
    }, {
      validator: PasswordValidation.MatchPassword('password', 'confirmPassword')
    });
  }

  ngOnInit() {
    this._userRegistrationService.getTargetGroups().subscribe((response) => {
      const filteredArr = response.filter(el => (el !== AppConstants.internalSL
                                              && el !== AppConstants.internalBranch
                                              && el !== AppConstants.subsidiary
                                              && el !== AppConstants.licensee
                                              && el !== AppConstants.qualityPartner));

      this.targetCategories = filteredArr;
      this.listOfCategories = this._utilsService.initializeSelectOrMultiselect(filteredArr);
    });
    this._userRegistrationService.getSalutations().subscribe((response) => {
      const filteredTitles = response.filter((title) =>
        !AppConstants.disabledSalutationCodes.includes(title)
      );
      this.englishTitles = filteredTitles;
      this.listOfTitles = this._utilsService.initializeListOfSalutations(filteredTitles);
    });
    this._userRegistrationService.getLanguages().subscribe((response) => {
      this.languages = response;
      this.listOfLanguages = this._utilsService.initializeSelectOrMultiselect(response);
    });
    this._userRegistrationService.getCountries().subscribe((response) => {
      this.countries = response;
      this.listOfCountries = this._utilsService.initializeSelectOrMultiselect(response);
    });

    this._activatedRoute.queryParams
      .subscribe(params => {

        if (params['language'] && params['language'] != 'null') {
          this.selectedLanguage = this._getCurrentLanguageData(params['language']);
          this.onLanguageChange();
        } else {
          this.selectedLanguage = AppConstants.globalLanguageSelectorData[0];
          this.onLanguageChange(this.whatLanguage);
        }

        if (params['countryCode'] && params['countryCode'] != 'null') {
          this.selectedCountry = this._getCurrentCountryData(params['countryCode']);
          this.onCountryChange();
        } else {
          this.selectedCountry = AppConstants.globalCountrySelectorData[0];
          this.onCountryChange(this.whatCountry);
        }

      });

    /**
     *     @description Customer validators:
     *     - 'customerNumber' is required for “Aktiv Partner” or “Glas Faltwand Partner”, it’s optional for all other target groups.
     *     - 'customerName' is required for all target groups except “Private Customer” and “Other”.
     *     - if 'privateCustomer' - disable 'customerName'
     *     - if 'others' - disabled 'customerName' & 'customerNumber'
     */
    this.form.controls.customerCategory.valueChanges.subscribe(
      (customerCategory: string) => {
        this.form.controls.customerName.setValue(null);
        this.form.controls.customerNumber.setValue(null);

        if (customerCategory === AppConstants.others)
        {
          this.customerNumberReadonly = true;
          this.customerNameReadonly = true;
          this.form.controls.customerNumber.setValidators(Validators.compose([Validators.pattern(this._utilsService.getOnlyNumbersRegex()), Validators.maxLength(10)]));
          this.form.controls.customerName.setValidators([]);

          this.postalCodeRequired = false;
          this.phoneNumberRequired = false;
          this.setupPhoneValidators(false);
          this.setupPostalCodeValidators(false);
        }
        else if (customerCategory === AppConstants.privateCustomer)
        {
          this.form.controls.customerNumber.markAsUntouched();
          this.customerNameReadonly = true;
          this.customerNumberReadonly = false;
          this.form.controls.customerNumber.setValidators(Validators.compose([Validators.pattern(this._utilsService.getOnlyNumbersRegex()), Validators.maxLength(10)]));
          this.form.controls.customerName.setValidators([]);

          this.postalCodeRequired = false;
          this.phoneNumberRequired = false;
          this.setupPhoneValidators(false);
          this.setupPostalCodeValidators(false);
        }
        else if (customerCategory === AppConstants.qualityPartner)
        {
          this.form.controls.customerName.markAsUntouched();
          this.form.controls.customerNumber.markAsUntouched();
          this.customerNumberReadonly = false;
          this.customerNameReadonly = false;
          this.form.controls.customerName.setValidators(Validators.required);
          this.form.controls.customerNumber.setValidators(Validators.compose([Validators.required, Validators.pattern(this._utilsService.getOnlyNumbersRegex()), Validators.maxLength(10)]));

          this.postalCodeRequired = false;
          this.phoneNumberRequired = true;
          this.setupPhoneValidators(true);
          this.setupPostalCodeValidators(false);
        }
        else if(customerCategory === AppConstants.architectPlanner)
        {
          this.form.controls.customerName.markAsUntouched();
          this.form.controls.customerNumber.markAsUntouched();
          this.customerNumberReadonly = false;
          this.customerNameReadonly = false;
          this.customerNumberRequired = false;
          this.customerNameRequired = true;
          this.form.controls.customerName.setValidators(Validators.required);
          this.form.controls.customerNumber.setValidators(Validators.compose([Validators.pattern(this._utilsService.getOnlyNumbersRegex()), Validators.maxLength(10)]));

          this.postalCodeRequired = true;
          this.phoneNumberRequired = true;
          this.setupPhoneValidators(true);
          this.setupPostalCodeValidators(true);
        }
        else
        {
          this.form.controls.customerName.markAsUntouched();
          this.form.controls.customerNumber.markAsUntouched();
          this.customerNumberReadonly = false;
          this.customerNameReadonly = false;
          this.customerNameRequired = true;
          this.form.controls.customerName.setValidators(Validators.required);
          this.form.controls.customerNumber.setValidators(Validators.compose([Validators.pattern(this._utilsService.getOnlyNumbersRegex()), Validators.maxLength(10)]));

          this.postalCodeRequired = false;
          this.phoneNumberRequired = true;
          this.setupPhoneValidators(true);
          this.setupPostalCodeValidators(false);
        }

        this.form.controls.customerName.updateValueAndValidity();
        this.form.controls.customerNumber.updateValueAndValidity();
        this.form.controls.phoneNumber.updateValueAndValidity();
        this.form.controls.postalCode.updateValueAndValidity();
      }
    );
    this.siteKey = environment.recaptcha.siteKey;
  }

  public getEnglishLanguage() {
    return this.englishLanguage;
  }

  public getGermanLanguage() {
    return this.germanLanguage;
  }

  public getFrenchLanguage() {
    return this.frenchLanguage;
  }

  public getDutchLanguage() {
    return this.dutchLanguage;
  }

  public getListOfCategories() {
    return this.listOfCategories;
  }

  public getListOfTitles() {
    return this.listOfTitles;
  }

  public getListOfLanguages() {
    return this.listOfLanguages;
  }

  public getListOfCountries() {
    return this.listOfCountries;
  }

  public getDataPrivacyNotice(): Observable<DataPrivacyNotice> {
    return this.dataPrivacyNotice;
  }

  public onLanguageChange(language?: string) {
    this._utilsService.setDefaultLanguage(language ? language : this.selectedLanguage.value);
  }

  public onCountryChange(country?: string) {

  }

  private setupPhoneValidators(required) {
    this.form.controls.phoneNumber.setValidators([]);
    if (required) {
      this.form.controls.phoneNumber.setValidators([
        Validators.required,
        Validators.pattern(this._utilsService.getPhoneNumberRegex()),
        Validators.maxLength(20)
      ]);
    } else {
      this.form.controls.phoneNumber.setValidators([
        Validators.pattern(this._utilsService.getPhoneNumberRegex()),
        Validators.maxLength(20)
      ]);
    }
    this.form.controls.phoneNumber.markAsUntouched();
  }

  private setupPostalCodeValidators(required) {
    this.form.controls.postalCode.setValidators([]);
    if (required) {
      this.form.controls.postalCode.setValidators([
        Validators.required,
        Validators.maxLength(10),
        Validators.minLength(2),
        Validators.pattern(this._utilsService.getPostalCodeRegex())
      ]);
    } else {
      this.form.controls.postalCode.setValidators([
        Validators.maxLength(10),
        Validators.minLength(2),
        Validators.pattern(this._utilsService.getPostalCodeRegex())
      ]);
    }
    this.form.controls.postalCode.markAsUntouched();
  }

  private _getCurrentLanguageData(languageValue) {
    let currentLanguageData = AppConstants.globalLanguageSelectorData[0];
    this.languageSelectorData.forEach(item => {
      if (item.value == languageValue) {
        currentLanguageData = item;
      }
    });
    return currentLanguageData;
  }

  private _getCurrentCountryData(countryValue) {
    let currentCountryData = AppConstants.globalCountrySelectorData[0];
    this.countrySelectorData.forEach(item => {
      if (item.value == countryValue) {
        currentCountryData = item;
      }
    });
    return currentCountryData;
  }

  /**
   * @description Handler for form submit
   * @param formValues {Object} - form values
   */
  save(form: UntypedFormGroup, token: string) {

    form.controls['gRecaptchaResponse'].setValue(token);
    const newUser = form.value;

    // Convert empty/blank customerNumber to null
    if (newUser.customerNumber === '' || (typeof newUser.customerNumber === 'string' && newUser.customerNumber.trim() === '')) {
      newUser.customerNumber = null;
    }

    this.loading = true;
    this._userRegistrationService.saveUser(newUser).subscribe((response) => {
      console.log("response: " + response);

      this.loading = false;
      this.user = response;
      this.form.reset();
      this._route.navigate([AppConstants.confirmationPage], {
        queryParams: {
          origin: AppConstants.userRegistrationPath,
          language: this.selectedLanguage.value,
          country: this.selectedCountry.value
        }
      });
    }, (error) => {
      this.loading = false;
      // The backend answers registration failures with an empty body, so reading error.message off it yields
      // undefined and the user would be left without any feedback. openErrorModal handles both a message object
      // and a plain string, and falls back to a translated generic message when there is nothing to show.
      this.errorMessageService.openErrorModal(error);
    });
  }

  public send(form: UntypedFormGroup): void {
    if (form.invalid) {
      for (const control of Object.keys(form.controls)) {
        form.controls[control].markAsTouched();
      }
      return;
    }

    this.recaptchaV3Service.execute(this.siteKey, 'registration', (token) => {
        this.save(form, token);
    });
  }

  public addWhitespacePrefixIfNotDot(text: string): string {
    return text === '.' ? '.' : ' ' + text;
  }
}
